import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  CircularProgress,
} from '@mui/material';

import { useOrderService } from '@/hooks/useOrderService';
import { useUser } from '@/context/UserContext';
import { getUserEmail, getUserId } from '@/providers/authProvider';
import type { Order, Product } from '@/types';
import { OrderForm } from '@/pages/orders/form/OrderForm';
import { adaptOrdersHandoff, createNewOrderDraft } from '@/services/orders/orderService';
import { fromOrdersHandoffRouteState } from '@/providers/stateAdapters';

interface OrderDetailsPageProps {
  mode?: 'details' | 'new';
}

export const OrderDetailsPage: React.FC<OrderDetailsPageProps> = ({ mode = 'details' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { getBusinessUnit, userData } = useUser();
  const { loadOrderByNumber, loadOrderProductsByNumber, saveOrder, saveOrderProducts, deleteOrderProduct } = useOrderService();

  const [order, setOrder] = useState<Order | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isNewMode = mode === 'new';

  const handleSaveOrder = async (nextOrder: Order, options?: { deletedProducts?: Product[] }) => {
    const deletedProducts = (options?.deletedProducts ?? []).filter((product) => String(product.itemNumber ?? '').trim().length > 0);
    const orderNumber = nextOrder.orderNumber ?? id;
    if (!orderNumber) {
      if (isNewMode) {
        const savedOrderResult = await saveOrder(nextOrder);
        const savedOrder = savedOrderResult.order;
        const savedOrderNumber = savedOrder?.orderNumber ?? savedOrder?.orderId;
        const productsForNewOrder = (nextOrder.products ?? []).map((product) => ({
          ...product,
          itemNumber: undefined,
        }));
        let savedProducts: Product[] = nextOrder.products ?? [];

        if (savedOrderNumber) {
          const savedProductsResult = await saveOrderProducts(savedOrderNumber, savedOrder ?? nextOrder, productsForNewOrder);
          savedProducts = savedProductsResult.products.length > 0
            ? savedProductsResult.products
            : (nextOrder.products ?? []);
        }

        if (savedOrder) {
          setOrder({
            ...savedOrder,
            products: savedProducts,
          });
          setProducts(savedProducts);
        }

        const newOrderPreviousUrl = localStorage.getItem('previousUrl') ?? '';
        if (newOrderPreviousUrl.includes('inventory-management')) {
          navigate('/inventory-management');
          return;
        }
        navigate('/orders');
        return;
      }

      throw new Error('Order number is required.');
    }

    const savedOrderResult = await saveOrder(nextOrder);
    const existingItemNumbers = new Set(
      (products ?? [])
        .map((product) => String(product.itemNumber ?? '').trim())
        .filter(Boolean),
    );
    const normalizedProductsForSave = (nextOrder.products ?? []).map((product) => {
      const itemNumber = String(product.itemNumber ?? '').trim();
      if (!itemNumber || existingItemNumbers.has(itemNumber)) {
        return product;
      }

      return {
        ...product,
        itemNumber: undefined,
      };
    });

    const savedProductsResult = await saveOrderProducts(orderNumber, nextOrder, normalizedProductsForSave);
    const savedProducts = savedProductsResult.products.length > 0
      ? savedProductsResult.products
      : (nextOrder.products ?? []);

    if (deletedProducts.length > 0) {
      await Promise.all(
        deletedProducts.map((product) => deleteOrderProduct(orderNumber, String(product.itemNumber ?? '').trim())),
      );
    }

    if (savedOrderResult.order) {
      setOrder({
        ...savedOrderResult.order,
        products: savedProducts,
      });
      setProducts(savedProducts);
    }

    const previousUrl = localStorage.getItem('previousUrl') ?? '';
    if (previousUrl.includes('inventory-management')) {
      navigate('/inventory-management');
      return;
    }

    navigate('/orders');
  };

  useEffect(() => {
    let mounted = true;

    const loadOrderDetails = async () => {
      if (!getBusinessUnit()) {
        navigate('/');
        return;
      }

      if (isNewMode) {
        const creatorEmail = userData.email || await getUserEmail();
        const creatorId = creatorEmail || userData.userId || await getUserId();
        setOrder(createNewOrderDraft(creatorId));
        setProducts([]);
        setLoading(false);
        setError(null);
        return;
      }

      const routeState = fromOrdersHandoffRouteState(location.state);
      const handoff = adaptOrdersHandoff(routeState);
      const orderId = id || handoff?.orderId;

      if (!orderId) {
        setError('Order number is required.');
        setLoading(false);
        return;
      }

      if (handoff?.previousUrl) {
        localStorage.setItem('previousUrl', handoff.previousUrl);
      }

      try {
        setLoading(true);
        setError(null);

        const orderResult = await loadOrderByNumber(orderId);

        if (!mounted) {
          return;
        }

        if (!orderResult.order) {
          setOrder(null);
          setProducts([]);
          setError(`Order ${orderId} was not found.`);
          return;
        }

        let productsResult: Product[] = [];
        try {
          const loadedOrderNumber = orderResult.order.orderNumber || orderResult.order.orderId || orderId;
          const productsResponse = await loadOrderProductsByNumber(loadedOrderNumber);
          productsResult = productsResponse.products;
        } catch {
          productsResult = [];
        }

        setOrder({
          ...orderResult.order,
          products: productsResult,
        });
        setProducts(productsResult);
      } catch {
        if (!mounted) {
          return;
        }
        setOrder(null);
        setProducts([]);
        setError('Unable to load order details.');
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    void loadOrderDetails();

    return () => {
      mounted = false;
    };
  }, [
    id,
    isNewMode,
    loadOrderByNumber,
    loadOrderProductsByNumber,
    getBusinessUnit,
    location.state,
    navigate,
    userData.email,
    userData.userId,
  ]);

  if (loading) {
    return (
      <Box sx={{ p: 0 }}>
        <CircularProgress className="loading-spinner" sx={{ color: '#0c479d' }} size={50} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0 }}>
      {error ? (
        <Box sx={{ p: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      ) : null}

      {!error && order ? (
        <OrderForm
          order={{
            ...order,
            products,
          }}
          onBack={() => navigate('/orders')}
          onSave={handleSaveOrder}
        />
      ) : null}
    </Box>
  );
};
